import { FaExclamationTriangle } from 'react-icons/fa';
import { terminology } from '../config/terminology';

const formatDate = (value) => {
  if (!value) return 'N/A';
  const date = typeof value === 'string'
    ? new Date(value)
    : typeof value.toDate === 'function'
      ? value.toDate()
      : new Date((value._seconds ?? value.seconds ?? 0) * 1000);
  return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Field = ({ label, children }) => (
  <div><span className="font-medium">{label}:</span> {children}</div>
);

/**
 * Advisory warning shown when an admin completes a job that looks like a duplicate of one already
 * completed on the same schedule within the past week. Follows the duplicate-schedule warning on
 * AddJobSchedulePage: it states the conflict and lets the admin complete anyway.
 */
const DuplicateJobWarningModal = ({
  isOpen,
  duplicates,
  jobSummary,
  submitting,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const count = duplicates?.length || 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-full overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center mb-4">
            <FaExclamationTriangle className="text-amber-500 mr-2 flex-shrink-0" />
            Possible duplicate job
          </h3>

          <div className="border border-amber-300 bg-amber-50 rounded-md p-4 text-left mb-4">
            <p className="text-sm text-amber-800">
              {count === 1 ? 'Another job' : `${count} other jobs`} on this schedule for the same{' '}
              {terminology.household.singular.toLowerCase()} {count === 1 ? 'was' : 'were'} completed
              within 7 days, sharing at least one service. You can still complete this job.
            </p>
          </div>

          <div className="bg-white rounded border border-gray-200 p-3 mb-3">
            <h5 className="text-xs font-semibold uppercase text-gray-500 mb-2">This job (being completed)</h5>
            <div className="text-sm text-gray-700 space-y-1">
              <Field label={terminology.household.singular}>{jobSummary?.householdName || 'N/A'}</Field>
              {jobSummary?.address && <Field label="Address">{jobSummary.address}</Field>}
              <Field label="Services">{jobSummary?.services || 'N/A'}</Field>
              <Field label="Completion Date">{formatDate(jobSummary?.completionDate)}</Field>
            </div>
          </div>

          <h5 className="text-xs font-semibold uppercase text-amber-700 mb-2">
            Already completed ({count})
          </h5>
          {/* Inner scroll keeps a long list from pushing the buttons off screen. */}
          <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {(duplicates || []).map((dup) => (
              <div key={dup.id} className="bg-white rounded border border-amber-200 p-3">
                <div className="text-sm text-gray-700 space-y-1">
                  <Field label="Services">
                    {(dup.services || []).map((s) => s.name).filter(Boolean).join(', ') || 'N/A'}
                  </Field>
                  <Field label="Completion Date">{formatDate(dup.completionDate)}</Field>
                  <Field label="Status">{dup.status || 'N/A'}</Field>
                  {dup.actualHours != null && <Field label="Actual Hours">{dup.actualHours}</Field>}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onCancel}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-gray-300 rounded-md shadow-sm hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-500 border border-amber-600 rounded-md shadow-sm hover:bg-amber-600 disabled:opacity-50"
            >
              {submitting ? 'Completing…' : 'Complete anyway'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateJobWarningModal;
